/**
 * ProfitSpot — Admin Analytics Dashboard
 * Platform health metrics, user stats, revenue intelligence
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getBackendBaseUrl } from '../lib/apiBase';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  Users, TrendingUp, DollarSign, RefreshCw, Activity,
  Crown, Clock, Zap, ShieldCheck, BarChart3, UserCheck,
  ArrowUpRight, Layers, AlertTriangle
} from 'lucide-react';

const API_URL = getBackendBaseUrl();

const TIER_COLORS = {
  free: '#4b5563',
  starter: '#00d4ff',
  pro: '#ffa502',
  unlimited: '#00ff88',
};

const TIER_LABELS = {
  free: 'Free',
  starter: 'Free ($0)',
  pro: 'Pro ($60)',
  unlimited: 'Unlimited ($90)',
};

// ─── Stat Card ───────────────────────────────────────────────
const StatCard = ({ label, value, sub, icon: Icon, accent = '#00ff88', trend }) => (
  <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-5 flex flex-col gap-3 hover:border-gray-700 transition-colors" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">{label}</span>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
    </div>
    <div>
      <div className="text-3xl font-bold text-white">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
    {trend !== undefined && (
      <div className={`flex items-center gap-1 text-xs font-medium ${trend >= 0 ? 'text-[#00ff88]' : 'text-red-400'}`}>
        <ArrowUpRight className="w-3 h-3" />
        {trend >= 0 ? '+' : ''}{trend} this week
      </div>
    )}
  </div>
);

// ─── Custom Tooltip ──────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg px-3 py-2 text-xs">
      <p className="text-gray-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-bold">{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

// ─── Tier Badge ──────────────────────────────────────────────
const TierBadge = ({ tier }) => (
  <span
    className="text-[10px] px-2 py-0.5 rounded font-bold uppercase"
    style={{ backgroundColor: `${TIER_COLORS[tier] || '#4b5563'}20`, color: TIER_COLORS[tier] || '#9ca3af' }}
  >
    {tier || 'free'}
  </span>
);

// ─── Main Component ──────────────────────────────────────────
export default function AdminDashboard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setData(await res.json());
        setLastRefresh(new Date());
      } else {
        toast.error('Failed to load analytics');
      }
    } catch {
      toast.error('Analytics request failed');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Tier pie chart data
  const tierChartData = data
    ? Object.entries(data.tiers)
        .filter(([, v]) => v > 0)
        .map(([tier, count]) => ({
          name: TIER_LABELS[tier] || tier,
          value: count,
          fill: TIER_COLORS[tier] || '#4b5563',
        }))
    : [];

  return (
    <div className="min-h-screen bg-[#0a0a12] p-4 md:p-6" data-testid="admin-dashboard">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="w-5 h-5 text-[#00ff88]" />
            <h1 className="text-xl md:text-2xl font-bold text-white">Analytics Dashboard</h1>
            <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded font-bold uppercase">Admin</span>
          </div>
          <p className="text-sm text-gray-500">
            Platform health &amp; revenue intelligence
            {lastRefresh && (
              <span className="ml-2 text-gray-600">
                · Updated {lastRefresh.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={fetchAnalytics}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-[#00ff88]/10 text-[#00ff88] border border-[#00ff88]/20 rounded-lg text-sm font-medium hover:bg-[#00ff88]/20 transition-colors disabled:opacity-50"
          data-testid="refresh-analytics-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <RefreshCw className="w-10 h-10 animate-spin text-[#00ff88] mx-auto mb-4" />
            <p className="text-gray-400 text-sm">Loading analytics...</p>
          </div>
        </div>
      ) : data ? (
        <div className="space-y-6">
          {/* ── Primary Metrics Row ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Total Users"
              value={data.total_users.toLocaleString()}
              sub={`${data.admin_users} admin${data.admin_users !== 1 ? 's' : ''}`}
              icon={Users}
              accent="#00d4ff"
              trend={data.new_this_week}
            />
            <StatCard
              label="Est. Monthly Revenue"
              value={`$${data.estimated_mrr.toLocaleString()}`}
              sub="MRR from paid tiers"
              icon={DollarSign}
              accent="#00ff88"
            />
            <StatCard
              label="Active Trials"
              value={data.active_trials}
              sub="7-day free trials"
              icon={Clock}
              accent="#ffa502"
            />
            <StatCard
              label="Conversion Rate"
              value={`${data.conversion_rate}%`}
              sub="Free → Paid"
              icon={TrendingUp}
              accent={data.conversion_rate > 30 ? '#00ff88' : '#ffa502'}
            />
          </div>

          {/* ── Secondary Metrics Row ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="New Today"
              value={data.new_today}
              sub="Users registered today"
              icon={UserCheck}
              accent="#00ff88"
            />
            <StatCard
              label="New This Week"
              value={data.new_this_week}
              sub="Last 7 days"
              icon={Zap}
              accent="#00d4ff"
            />
            <StatCard
              label="New This Month"
              value={data.new_this_month}
              sub="Last 30 days"
              icon={Activity}
              accent="#ffa502"
            />
            <StatCard
              label="Paid Users"
              value={(data.tiers.starter + data.tiers.pro + data.tiers.unlimited)}
              sub={`Pro: ${data.tiers.pro} · Unlimited: ${data.tiers.unlimited}`}
              icon={Crown}
              accent="#ffa502"
            />
          </div>

          {/* ── Charts Row ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* User Growth Chart */}
            <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-5">
                <TrendingUp className="w-4 h-4 text-[#00ff88]" />
                <h3 className="text-sm font-bold text-white">User Growth (8 Weeks)</h3>
              </div>
              {data.growth_data?.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={data.growth_data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="grad-users" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#00ff88" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#00ff88" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
                    <XAxis dataKey="week" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="users"
                      name="Users"
                      stroke="#00ff88"
                      strokeWidth={2}
                      fill="url(#grad-users)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-48 text-gray-600 text-sm">No growth data yet</div>
              )}
            </div>

            {/* Tier Distribution Chart */}
            <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-5">
                <Layers className="w-4 h-4 text-[#ffa502]" />
                <h3 className="text-sm font-bold text-white">Tier Distribution</h3>
              </div>
              {tierChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={tierChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {tierChartData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} stroke="transparent" />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      formatter={(value) => <span style={{ color: '#9ca3af', fontSize: '11px' }}>{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-48 text-gray-600 text-sm">No users yet</div>
              )}
            </div>
          </div>

          {/* ── Tier Breakdown Table ── */}
          <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-5">
              <ShieldCheck className="w-4 h-4 text-[#00d4ff]" />
              <h3 className="text-sm font-bold text-white">Tier Breakdown</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(data.tiers).map(([tier, count]) => {
                const prices = { free: 0, starter: 15, pro: 60, unlimited: 90 };
                const revenue = count * (prices[tier] || 0);
                return (
                  <div
                    key={tier}
                    className="bg-[#0a0a12] rounded-lg p-4 border border-gray-800 text-center"
                    data-testid={`tier-stat-${tier}`}
                  >
                    <TierBadge tier={tier} />
                    <div className="text-2xl font-bold text-white mt-2">{count}</div>
                    <div className="text-xs text-gray-500 mt-0.5">users</div>
                    {revenue > 0 && (
                      <div className="text-xs text-[#00ff88] font-bold mt-1">${revenue}/mo</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Platform Status Banner ── */}
          <div className="bg-[#0f0f1a] border border-[#00ff88]/20 rounded-xl p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[#00ff88] animate-pulse" />
                <div>
                  <p className="text-sm font-bold text-white">Platform Operational</p>
                  <p className="text-xs text-gray-500">All systems nominal · Last checked {new Date(data.timestamp).toLocaleTimeString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-400">
                <span>Target MRR: <span className="text-white font-bold">$11,400</span></span>
                <span>Progress: <span className="text-[#00ff88] font-bold">{Math.round((data.estimated_mrr / 11400) * 100)}%</span></span>
              </div>
            </div>

            {/* MRR Progress bar */}
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                <span>MRR Progress to Goal</span>
                <span>${data.estimated_mrr.toLocaleString()} / $11,400</span>
              </div>
              <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#00d4ff] to-[#00ff88] rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(100, (data.estimated_mrr / 11400) * 100)}%` }}
                  data-testid="mrr-progress-bar"
                />
              </div>
            </div>
          </div>

          {/* Preview Environment Banner */}
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-purple-300 font-medium">Preview Environment</p>
              <p className="text-xs text-purple-400/70 mt-1">
                User data shown reflects only this preview environment's database, not production. 
                Revenue and growth metrics are based on test accounts created during development. 
                In production, these will show real platform statistics.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-32 text-gray-500">Failed to load analytics data</div>
      )}
    </div>
  );
}
